import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Clock, 
  Calendar, 
  Bus, 
  User, 
  CheckCircle,
  XCircle,
  Loader2,
  Search,
  ChevronDown,
  Navigation,
  X,
  CreditCard
} from 'lucide-react';
import Swal from 'sweetalert2';
import { routeApi, scheduleApi, bookingApi, customerApi, seatApi, routeCityApi } from '../services/api';
import { getSriLankaDateString, getMinSelectableDate, getMaxSelectableDate } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';

const AdminBooking = () => {
  const navigate = useNavigate();
  const { user, isRole, ROLES } = useAuth();

  // Check if user is Manager/Admin
  useEffect(() => {
    if (!user || (!isRole(ROLES.MANAGER))) {
      Swal.fire({
        icon: 'error',
        title: 'Access Denied',
        text: 'Only Administrators can access this page.',
        confirmButtonColor: '#dc2626'
      }).then(() => {
        navigate('/manager');
      });
    }
  }, [user, isRole, ROLES, navigate]);

  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengerDetails, setPassengerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    gender: '',
    nic: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('Cash'); // Cash, Bank Transfer, or Collect from Bus
  const [pickupLocationId, setPickupLocationId] = useState('');
  const [dropoffLocationId, setDropoffLocationId] = useState('');
  const [routeCities, setRouteCities] = useState([]);
  const [routeCitiesLoading, setRouteCitiesLoading] = useState(false);
  
  // Route and Date Selection States
  const [selectedRouteId, setSelectedRouteId] = useState('');
  const [selectedDate, setSelectedDate] = useState(getSriLankaDateString());
  const [routes, setRoutes] = useState([]);
  const [routesLoading, setRoutesLoading] = useState(true);
  const [journeyDetails, setJourneyDetails] = useState(null);
  const [loadingJourney, setLoadingJourney] = useState(false);
  const [availableSchedules, setAvailableSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(null);
  
  // Searchable dropdown states for route
  const [routeSearchTerm, setRouteSearchTerm] = useState('');
  const [routeDropdownOpen, setRouteDropdownOpen] = useState(false);
  const routeDropdownRef = useRef(null);
  
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(false);

  // Generate seat layout (reuse logic from BusBooking)
  const generateSeats = (seatLayout, bookedSeats = [], notAvailableSeats = []) => {
    const seats = [];
    
    const notAvailableSeatNumbers = notAvailableSeats
      .map(seat => seat.toString().trim())
      .filter(seat => seat !== '')
      .map(seat => parseInt(seat))
      .filter(seat => !isNaN(seat));
    
    // Check if this is the special 51 Layout
    if (seatLayout && seatLayout.toString().toLowerCase().includes('51 layout')) {
      // 51 Layout: Special arrangement - only actual seats, no empty cells
      // Seat 51 in the middle between left and right blocks
      const seatPositions = [
        // Row 1: Seat 51 in middle
        { row: 1, col: 1, seatNum: 47 }, { row: 1, col: 2, seatNum: 48 },
        { row: 1, col: 3, seatNum: 51 }, // Seat 51 in middle
        { row: 1, col: 4, seatNum: 50 }, { row: 1, col: 5, seatNum: 49 },
        // Row 2
        { row: 2, col: 1, seatNum: 41 }, { row: 2, col: 2, seatNum: 42 },
        { row: 2, col: 3, seatNum: null }, // Aisle (skip)
        { row: 2, col: 4, seatNum: 46 }, { row: 2, col: 5, seatNum: 45 },
        // Row 3
        { row: 3, col: 1, seatNum: 37 }, { row: 3, col: 2, seatNum: 38 },
        { row: 3, col: 3, seatNum: null }, // Aisle (skip)
        { row: 3, col: 4, seatNum: 44 }, { row: 3, col: 5, seatNum: 43 },
        // Row 4
        { row: 4, col: 1, seatNum: 33 }, { row: 4, col: 2, seatNum: 34 },
        { row: 4, col: 3, seatNum: null }, // Aisle (skip)
        { row: 4, col: 4, seatNum: 40 }, { row: 4, col: 5, seatNum: 39 },
        // Row 5
        { row: 5, col: 1, seatNum: 29 }, { row: 5, col: 2, seatNum: 30 },
        { row: 5, col: 3, seatNum: null }, // Aisle (skip)
        { row: 5, col: 4, seatNum: 36 }, { row: 5, col: 5, seatNum: 35 },
        // Row 6
        { row: 6, col: 1, seatNum: 25 }, { row: 6, col: 2, seatNum: 26 },
        { row: 6, col: 3, seatNum: null }, // Aisle (skip)
        { row: 6, col: 4, seatNum: 32 }, { row: 6, col: 5, seatNum: 31 },
        // Row 7
        { row: 7, col: 1, seatNum: 21 }, { row: 7, col: 2, seatNum: 22 },
        { row: 7, col: 3, seatNum: null }, // Aisle (skip)
        { row: 7, col: 4, seatNum: 28 }, { row: 7, col: 5, seatNum: 27 },
        // Row 8
        { row: 8, col: 1, seatNum: 17 }, { row: 8, col: 2, seatNum: 18 },
        { row: 8, col: 3, seatNum: null }, // Aisle (skip)
        { row: 8, col: 4, seatNum: 24 }, { row: 8, col: 5, seatNum: 23 },
        // Row 9
        { row: 9, col: 1, seatNum: 13 }, { row: 9, col: 2, seatNum: 14 },
        { row: 9, col: 3, seatNum: null }, // Aisle (skip)
        { row: 9, col: 4, seatNum: 20 }, { row: 9, col: 5, seatNum: 19 },
        // Row 10
        { row: 10, col: 1, seatNum: 9 }, { row: 10, col: 2, seatNum: 10 },
        { row: 10, col: 3, seatNum: null }, // Aisle (skip)
        { row: 10, col: 4, seatNum: 16 }, { row: 10, col: 5, seatNum: 15 },
        // Row 11
        { row: 11, col: 1, seatNum: 5 }, { row: 11, col: 2, seatNum: 6 },
        { row: 11, col: 3, seatNum: null }, // Aisle (skip)
        { row: 11, col: 4, seatNum: 12 }, { row: 11, col: 5, seatNum: 11 },
        // Row 12
        { row: 12, col: 1, seatNum: 1 }, { row: 12, col: 2, seatNum: 2 },
        { row: 12, col: 3, seatNum: null }, // Aisle (skip)
        { row: 12, col: 4, seatNum: 8 }, { row: 12, col: 5, seatNum: 7 },
        // Row 13
        { row: 13, col: 1, seatNum: null }, { row: 13, col: 2, seatNum: null },
        { row: 13, col: 3, seatNum: null }, // Aisle (skip)
        { row: 13, col: 4, seatNum: 4 }, { row: 13, col: 5, seatNum: 3 },
      ];
      
      // Process only actual seats (skip null/empty cells)
      seatPositions.forEach(({ row, col, seatNum }) => {
        if (seatNum !== null) {
          const seatNumberStr = seatNum.toString().padStart(2, '0');
          const isBooked = bookedSeats.some(bs => {
            if (!bs || !bs.seatNumber) return false;
            const bsSeatNumber = bs.seatNumber.toString().trim();
            return bsSeatNumber === seatNumberStr || 
                   bsSeatNumber === seatNum.toString() || 
                   parseInt(bsSeatNumber) === seatNum;
          });
          
          const bookedSeat = bookedSeats.find(bs => {
            if (!bs || !bs.seatNumber) return false;
            const bsSeatNumber = bs.seatNumber.toString().trim();
            return bsSeatNumber === seatNumberStr || 
                   bsSeatNumber === seatNum.toString() || 
                   parseInt(bsSeatNumber) === seatNum;
          });
          
          const isNotAvailable = notAvailableSeatNumbers.includes(seatNum);
          
          let isBookedByGents = false;
          let isBookedByLadies = false;
          let isAvailableSoon = false;
          
          if (isBooked && bookedSeat) {
            // Check for gender
            if (bookedSeat.gender) {
              const genderLower = bookedSeat.gender.toString().toLowerCase().trim();
              isBookedByGents = genderLower === 'male' || genderLower === 'm' || genderLower === 'gent' || genderLower === 'gents';
              isBookedByLadies = genderLower === 'female' || genderLower === 'f' || genderLower === 'lady' || genderLower === 'ladies';
            }
            
            // Check if PaymentStatus = 'Pending' AND Status = 'Pending'
            // Database columns: Bookings.PaymentStatus and Bookings.Status
            // Backend might return: paymentStatus (lowercase) and status (lowercase) or Status (capital)
            const paymentStatus = (bookedSeat.paymentStatus || bookedSeat.PaymentStatus || bookedSeat.Paymentstatus || bookedSeat.paymentstatus || '').toString().trim();
            const bookingStatus = (bookedSeat.status || bookedSeat.Status || bookedSeat.bookingStatus || bookedSeat.BookingStatus || bookedSeat.bookingstatus || '').toString().trim();
            
            // Debug log to check what fields are actually in the bookedSeat object
            console.log(`Seat ${seatNumberStr} bookedSeat object:`, bookedSeat);
            console.log(`Seat ${seatNumberStr}: paymentStatus="${paymentStatus}", status="${bookingStatus}"`);
            
            if (paymentStatus.toLowerCase() === 'pending' && bookingStatus.toLowerCase() === 'pending') {
              isAvailableSoon = true;
              console.log(`✅ Seat ${seatNumberStr} marked as Available Soon (Yellow) - paymentStatus: ${paymentStatus}, status: ${bookingStatus}`);
            }
          }
          
          seats.push({
            id: seatNumberStr,
            number: seatNumberStr,
            row: row,
            col: col,
            isAvailable: !isBooked && !isNotAvailable && !isAvailableSoon,
            isNotAvailable: isNotAvailable,
            isBookedByGents: isBookedByGents,
            isBookedByLadies: isBookedByLadies,
            isAvailableSoon: isAvailableSoon,
            isSelected: false
          });
        }
      });
      
      return seats;
    }
    
    // Standard layout parsing
    let columns = 4;
    let rows = 12;
    
    if (seatLayout) {
      const layoutMatch = seatLayout.toString().match(/(\d+)[x×](\d+)/i);
      if (layoutMatch) {
        columns = parseInt(layoutMatch[1]);
        rows = parseInt(layoutMatch[2]);
      }
    }
    
    let seatNumber = 1;
    
    for (let row = 1; row <= rows; row++) {
      for (let col = 1; col <= columns; col++) {
        const seatNumberStr = seatNumber.toString();
        const isBooked = bookedSeats.some(bs => {
          if (!bs || !bs.seatNumber) return false;
          const bsSeatNumber = bs.seatNumber.toString().trim();
          return bsSeatNumber === seatNumberStr || parseInt(bsSeatNumber) === seatNumber;
        });
        
        const bookedSeat = bookedSeats.find(bs => {
          if (!bs || !bs.seatNumber) return false;
          const bsSeatNumber = bs.seatNumber.toString().trim();
          return bsSeatNumber === seatNumberStr || parseInt(bsSeatNumber) === seatNumber;
        });
        
        const isNotAvailable = notAvailableSeatNumbers.includes(seatNumber);
        
        let isBookedByGents = false;
        let isBookedByLadies = false;
        let isAvailableSoon = false;
        
        if (isBooked && bookedSeat) {
          // Check for gender
          if (bookedSeat.gender) {
            const genderLower = bookedSeat.gender.toString().toLowerCase().trim();
            isBookedByGents = genderLower === 'male' || genderLower === 'm' || genderLower === 'gent' || genderLower === 'gents';
            isBookedByLadies = genderLower === 'female' || genderLower === 'f' || genderLower === 'lady' || genderLower === 'ladies';
          }
          
          // Check if Payment Status = 'Pending' AND Booking Status != 'Cancelled'
          const paymentStatus = (bookedSeat.paymentStatus || bookedSeat.PaymentStatus || '').toString().trim();
          const bookingStatus = (bookedSeat.bookingStatus || bookedSeat.BookingStatus || bookedSeat.status || bookedSeat.Status || '').toString().trim();
          
          if (paymentStatus.toLowerCase() === 'pending' && bookingStatus.toLowerCase() !== 'cancelled') {
            isAvailableSoon = true;
          }
        }
        
        seats.push({
          id: seatNumberStr,
          number: seatNumberStr,
          row: row,
          col: col,
          isAvailable: !isBooked && !isNotAvailable && !isAvailableSoon,
          isNotAvailable: isNotAvailable,
          isBookedByGents: isBookedByGents,
          isBookedByLadies: isBookedByLadies,
          isAvailableSoon: isAvailableSoon,
          isSelected: false
        });
        
        seatNumber++;
      }
    }
    return seats;
  };

  // Load routes on component mount
  useEffect(() => {
    const loadRoutes = async () => {
      try {
        setRoutesLoading(true);
        const response = await routeApi.getRoutes(true);
        const routesData = Array.isArray(response) ? response : (response?.value || []);
        setRoutes(routesData);
      } catch (error) {
        console.error('Error loading routes:', error);
      } finally {
        setRoutesLoading(false);
      }
    };
    loadRoutes();
  }, []);

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (routeDropdownRef.current && !routeDropdownRef.current.contains(event.target)) {
        setRouteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Process journey details when schedule is selected
  const processJourneyDetails = async (selectedSchedule, scheduleDate, route) => {
    setLoadingJourney(true);
    try {
      const vehicleId = selectedSchedule.busID || selectedSchedule.busId;
      const vehicleResponse = await scheduleApi.getSchedule(selectedSchedule.scheduleID || selectedSchedule.scheduleId);
      const schedule = vehicleResponse?.value || vehicleResponse;
      const vehicle = schedule?.vehicle || schedule?.Vehicle;

      if (!vehicle) {
        throw new Error('Vehicle information not found');
      }

      // Get booked seats for this schedule date (only SeatBooked=1)
      let bookedSeatNumbers = [];
      const scheduleDateId = scheduleDate.scheduleDateID || scheduleDate.scheduleDateId;
      
      if (scheduleDateId) {
        try {
          const scheduleDateResponse = await scheduleApi.getScheduleDate(scheduleDateId);
          const scheduleDateData = scheduleDateResponse?.value || scheduleDateResponse;
          
          let bookedSeatsArray = null;
          if (scheduleDateData?.bookedSeatNumbers) {
            bookedSeatsArray = scheduleDateData.bookedSeatNumbers;
          } else if (scheduleDateData?.data?.bookedSeatNumbers) {
            bookedSeatsArray = scheduleDateData.data.bookedSeatNumbers;
          }
          
          if (bookedSeatsArray && Array.isArray(bookedSeatsArray)) {
            if (bookedSeatsArray.length > 0 && typeof bookedSeatsArray[0] === 'object') {
              bookedSeatNumbers = bookedSeatsArray;
              // Log all booked seats received from backend
              console.log(`📋 Total booked seats received from backend: ${bookedSeatNumbers.length}`);
              bookedSeatNumbers.forEach((seat, index) => {
                console.log(`  [${index + 1}] Seat ${seat.seatNumber || seat.SeatNumber}: paymentStatus="${seat.paymentStatus || seat.PaymentStatus || 'N/A'}", status="${seat.status || seat.Status || 'N/A'}", gender="${seat.gender || seat.Gender || 'N/A'}"`);
              });
            } else {
              bookedSeatNumbers = bookedSeatsArray.map(s => ({
                seatNumber: s.toString().trim(),
                gender: null,
                paymentStatus: null,
                status: null
              }));
            }
          }
        } catch (error) {
          console.error('Could not fetch booked seat numbers:', error);
        }
      }

      const notAvailableSeats = vehicle.notAvailableSeatNumbers || 
        (vehicle.notAvailableSeat ? vehicle.notAvailableSeat.split(',').map(s => s.trim()).filter(s => s) : []);

      const seatLayout = vehicle.seatLayout || '4x12';
      const generatedSeats = generateSeats(seatLayout, bookedSeatNumbers, notAvailableSeats);
      setSeats(generatedSeats);

      setJourneyDetails({
        routeId: route.routeID || route.id,
        scheduleId: selectedSchedule.scheduleID || selectedSchedule.scheduleId,
        scheduleDateId: scheduleDate.scheduleDateID || scheduleDate.scheduleDateId,
        route: `${route.fromCity?.cityName || route.fromLocation || route.fromCityName} → ${route.toCity?.cityName || route.toLocation || route.toCityName}`,
        departure: scheduleDate.departureTime || scheduleDate.DepartureTime || 'N/A',
        arrival: scheduleDate.arrivalTime || scheduleDate.ArrivalTime || 'N/A',
        price: route.basePrice || 0,
        basePrice: route.basePrice || 0,
        serviceCharge: route.serviceCharge || 150,
        vehicleId: vehicleId,
        seatLayout: seatLayout
      });
    } catch (error) {
      console.error('Error processing journey details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to load journey details',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoadingJourney(false);
    }
  };

  // Load schedules when route and date are selected
  useEffect(() => {
    const loadSchedules = async () => {
      if (!selectedRouteId || !selectedDate) {
        setAvailableSchedules([]);
        setJourneyDetails(null);
        setSeats([]);
        return;
      }

      try {
        setLoadingJourney(true);
        const schedulesResponse = await scheduleApi.getSchedules();
        const allSchedules = Array.isArray(schedulesResponse) ? schedulesResponse : (schedulesResponse?.value || []);
        const routeSchedules = allSchedules.filter(s => 
          (s.routeID || s.routeId) == selectedRouteId && s.isActive !== false
        );

        const schedulesWithDates = [];
        for (const schedule of routeSchedules) {
          try {
            const scheduleDatesResponse = await scheduleApi.getScheduleDatesBySchedule(schedule.scheduleID || schedule.scheduleId);
            const scheduleDates = Array.isArray(scheduleDatesResponse) ? scheduleDatesResponse : (scheduleDatesResponse?.value || []);
            
            const matchingDate = scheduleDates.find(sd => {
              const dateStr = sd.date?.split('T')[0] || sd.date;
              return dateStr === selectedDate;
            });

            if (matchingDate) {
              schedulesWithDates.push({
                ...schedule,
                scheduleDate: matchingDate
              });
            }
          } catch (error) {
            console.error(`Error loading dates for schedule ${schedule.scheduleID}:`, error);
          }
        }

        setAvailableSchedules(schedulesWithDates);

        // Auto-select first schedule if available
        if (schedulesWithDates.length > 0 && !selectedScheduleId) {
          const firstSchedule = schedulesWithDates[0];
          const route = routes.find(r => (r.routeID || r.id) == selectedRouteId);
          if (route) {
            processJourneyDetails(firstSchedule, firstSchedule.scheduleDate, route);
            setSelectedScheduleId(firstSchedule.scheduleID || firstSchedule.scheduleId);
          }
        }
      } catch (error) {
        console.error('Error loading schedules:', error);
      } finally {
        setLoadingJourney(false);
      }
    };

    loadSchedules();
  }, [selectedRouteId, selectedDate, routes]);

  // Load RouteCities when route is selected
  useEffect(() => {
    const loadRouteCities = async () => {
      if (!selectedRouteId) {
        setRouteCities([]);
        setPickupLocationId('');
        setDropoffLocationId('');
        return;
      }

      try {
        setRouteCitiesLoading(true);
        const response = await routeCityApi.getRouteCitiesByRoute(selectedRouteId);
        const cities = response || [];
        // Sort by order
        const sortedCities = cities.sort((a, b) => (a.order || a.Order || 0) - (b.order || b.Order || 0));
        setRouteCities(sortedCities);
      } catch (error) {
        console.error('Error loading route cities:', error);
        setRouteCities([]);
      } finally {
        setRouteCitiesLoading(false);
      }
    };

    loadRouteCities();
  }, [selectedRouteId]);

  // Handle seat click
  const handleSeatClick = (seat) => {
    // Don't allow clicking on booked seats, unavailable seats, or available soon seats
    if (!seat.isAvailable || seat.isNotAvailable || seat.isAvailableSoon) {
      return;
    }
    
    // Toggle seat selection - allow deselecting
    if (selectedSeats.includes(seat.id)) {
      // Deselect seat
      setSelectedSeats(prev => prev.filter(s => s !== seat.id));
      setSeats(prev => prev.map(s => 
        s.id === seat.id ? { ...s, isSelected: false } : s
      ));
      } else {
      // Select seat
      setSelectedSeats(prev => [...prev, seat.id]);
      setSeats(prev => prev.map(s => 
        s.id === seat.id ? { ...s, isSelected: true } : s
      ));
      }
  };

  // Get seat class for styling
  const getSeatClass = (seat) => {
    // Selected seats - show in blue
    if (selectedSeats.includes(seat.id)) {
      return 'bg-blue-600 text-white cursor-pointer';
    }
    
    // Unavailable seats (not available for booking) - show in gray/ash color
    if (seat.isNotAvailable) {
      return 'bg-gray-400 text-gray-600 cursor-not-allowed opacity-60';
    }
    
    // Seats with Payment Status = 'Pending' AND Booking Status != 'Cancelled' - show in Yellow
    if (seat.isAvailableSoon) {
      return 'bg-yellow-500 text-white cursor-not-allowed opacity-90';
    }
    
    // Booked seats - show in red/orange (not gray), cannot be selected
    if (!seat.isAvailable) {
    if (seat.isBookedByGents) {
        return 'bg-red-500 text-white cursor-not-allowed opacity-75';
    }
    if (seat.isBookedByLadies) {
        return 'bg-pink-500 text-white cursor-not-allowed opacity-75';
    }
      return 'bg-red-500 text-white cursor-not-allowed opacity-75';
    }
    
    // Available seats - show in green
    return 'bg-green-500 text-white cursor-pointer';
  };

  // Handle booking submission
  const handleBooking = async () => {
    // Validate passenger details
    const missingFields = [];
    if (!passengerDetails.name || passengerDetails.name.trim() === '') {
      missingFields.push('Name');
    }
    if (!passengerDetails.phone || passengerDetails.phone.trim() === '') {
      missingFields.push('Phone Number');
    }
    if (!passengerDetails.gender || passengerDetails.gender.trim() === '') {
      missingFields.push('Gender');
    }

    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Required Information',
        html: `<p>Please fill in: ${missingFields.join(', ')}</p>`,
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Validate phone number
    const phoneRegex = /^[0-9]{10,}$/;
    const cleanPhone = passengerDetails.phone.replace(/[\s\-\(\)]/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Phone Number',
        text: 'Please enter a valid phone number (at least 10 digits).',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Validate journey and seats
    if (!journeyDetails || selectedSeats.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Selection',
        text: 'Please select seats to continue.',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    // Calculate total amount (including service charge with 15% discount)
    const originalServiceCharge = journeyDetails.serviceCharge || 150; // Original service charge per seat
    const basePrice = parseFloat(journeyDetails.basePrice) || 0; // Single ticket price per seat
    const noOfTiket = selectedSeats.length; // Number of tickets/seats
    
    // Apply 15% discount to service charge
    const serviceCharge = originalServiceCharge * 0.85; // Discounted service charge per seat (15% off)
    const totalServiceCharge = serviceCharge * noOfTiket; // Total discounted service charge for all tickets
    
    // TotalAmount = (TicketPrice + DiscountedServiceCharge) * number of seats
    const totalAmount = (basePrice + serviceCharge) * noOfTiket;

    try {
      setLoading(true);
      Swal.fire({
        title: 'Creating Booking...',
        text: 'Please wait while we process your booking.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Step 1: Find or create customer
      const customerData = {
        CustomerName: passengerDetails.name,
        Phone: passengerDetails.phone,
        Email: passengerDetails.email && passengerDetails.email.trim() !== '' ? passengerDetails.email.trim() : null,
        Gender: passengerDetails.gender,
        NIC: passengerDetails.nic && passengerDetails.nic.trim() !== '' ? passengerDetails.nic.trim() : null
      };

      const customerResponse = await customerApi.findOrCreateCustomer(customerData);
      let customer = customerResponse?.value || customerResponse?.data || customerResponse;
      let customerId = customer?.customerID || customer?.CustomerID;

      if (!customerId) {
        throw new Error('Failed to create or find customer');
      }

      // Step 2: Get vehicle ID
      let vehicleId = journeyDetails.vehicleId;
      if (!vehicleId) {
        const scheduleResponse = await scheduleApi.getSchedule(journeyDetails.scheduleId);
        const schedule = scheduleResponse?.value || scheduleResponse;
        vehicleId = schedule?.busID || schedule?.vehicleId || schedule?.vehicle?.id;
      }

      if (!vehicleId) {
        throw new Error('Could not determine vehicle ID for booking');
      }

      // Step 3: Create booking with AdminBooked = 1 and PaymentStatus based on payment method
      const travelDate = new Date(selectedDate + 'T00:00:00');
      
      // Set PaymentStatus based on payment method
      // "Collect from Bus" → Pending, others (Cash, Bank Transfer) → Completed
      const paymentStatus = paymentMethod === "Collect from Bus" ? "Pending" : "Completed";
      
      const bookingData = {
        CustomerID: parseInt(customerId),
        ScheduleDateID: parseInt(journeyDetails.scheduleDateId),
        SeatID: null,
        TravelDate: travelDate.toISOString(),
        SpecialRequests: `Payment Method: ${paymentMethod}`,
        PaymentStatus: paymentStatus, // Pending for "Collect from Bus", Completed for others
        TotalAmount: totalAmount,
        ServiceCharge: serviceCharge, // Single service charge per seat
        TicketPrice: basePrice, // Single ticket price per seat
        NoOfTiket: noOfTiket, // Number of tickets/seats
        TotalServiceCharge: totalServiceCharge, // Total service charge for all tickets (ServiceCharge * NoOfTiket)
        AdminBooked: 1, // Always 1 for admin bookings
        PaymentMethod: paymentMethod, // Store payment method
        PickupLocationID: pickupLocationId ? parseInt(pickupLocationId) : null,
        DropoffLocationID: dropoffLocationId ? parseInt(dropoffLocationId) : null
      };

      let bookingResponse;
      try {
        bookingResponse = await bookingApi.createBooking(bookingData);
        console.log('Booking response received:', bookingResponse);
      } catch (apiError) {
        console.error('API Error details:', {
          message: apiError.message,
          response: apiError.response,
          details: apiError.details,
          stack: apiError.stack
        });
        throw apiError;
      }

      // Handle different response formats
      // Backend returns: { BookingID, CustomerID, ... } (PascalCase)
      // Some APIs wrap in: { value: {...}, data: {...} }
      let booking = null;
      if (bookingResponse) {
        // Check if wrapped in value or data
        if (bookingResponse.value) {
          booking = bookingResponse.value;
        } else if (bookingResponse.data) {
          booking = bookingResponse.data;
        } else {
          // Direct response
          booking = bookingResponse;
        }
      }

      console.log('Extracted booking object:', booking);

      // Try multiple property name variations (camelCase and PascalCase)
      const bookingId = booking?.bookingID || booking?.BookingID || booking?.bookingId || booking?.id;

      if (!bookingId) {
        console.error('No booking ID found in response. Full response:', bookingResponse);
        console.error('Extracted booking object:', booking);
        throw new Error('Failed to create booking - no booking ID returned. Please check the console for details.');
      }

      console.log('Booking ID extracted:', bookingId);

      // Step 4: Create BookingSeats records (SeatBooked will be set to 1 for admin bookings)
      const createdBookingSeats = [];
      
      // Get seat numbers from selected seat IDs
      const selectedSeatNumbers = selectedSeats.map(seatId => {
        const seat = seats.find(s => s.id === seatId);
        return seat ? seat.number : seatId; // Fallback to seatId if seat not found
      });
      
      console.log('Creating BookingSeats for seats:', selectedSeatNumbers);
      
      for (const seatNumber of selectedSeatNumbers) {
        try {
          console.log(`Processing seat ${seatNumber}...`);
          
        const seatResponse = await seatApi.findOrCreateSeat({
          BusID: vehicleId,
          SeatNumber: seatNumber.toString(),
          SeatType: 'Standard',
          Price: basePrice
        });

        let seat = null;
        if (seatResponse?.value) {
          seat = seatResponse.value;
        } else if (Array.isArray(seatResponse)) {
          seat = seatResponse[0];
        } else if (typeof seatResponse === 'object') {
          seat = seatResponse;
        }

        const seatId = seat?.seatID || seat?.SeatID;
        if (!seatId) {
          throw new Error(`Failed to create or find seat ${seatNumber}`);
        }

          console.log(`Seat ${seatNumber} found/created with ID: ${seatId}`);

        const bookingSeatData = {
          BookingID: parseInt(bookingId),
          SeatID: parseInt(seatId),
          SeatNumber: seatNumber.toString(),
            Price: basePrice,
            SeatBooked: paymentStatus === "Completed" ? 1 : 0 // 1 if payment completed, 0 if pending
        };

          console.log(`Creating BookingSeat for seat ${seatNumber} with data:`, bookingSeatData);

        const bookingSeatResponse = await bookingApi.addBookingSeat(bookingId, bookingSeatData);
          console.log('BookingSeat response:', bookingSeatResponse);
          
        const bookingSeat = bookingSeatResponse?.value || bookingSeatResponse?.data || bookingSeatResponse;
        
        if (!bookingSeat) {
          throw new Error(`Failed to create BookingSeat for seat ${seatNumber}`);
        }
        
          console.log(`BookingSeat created successfully for seat ${seatNumber}`);
        createdBookingSeats.push(bookingSeat);
        } catch (seatError) {
          console.error(`Error processing seat ${seatNumber}:`, seatError);
          
          // Check if seat is already booked (availability conflict)
          const errorMessage = seatError?.message || seatError?.details?.error || seatError?.details?.message || '';
          const errorDetails = seatError?.details || seatError?.response?.data || {};
          const errorText = (typeof errorDetails === 'string' ? errorDetails : errorDetails.error || errorDetails.message || '').toLowerCase();
          const messageText = errorMessage.toLowerCase();
          
          // Check if this is a "seat already booked" error
          const isSeatAlreadyBooked = 
            messageText.includes('already booked') || 
            messageText.includes('seat is already') ||
            messageText.includes('seat already') ||
            errorText.includes('already booked') ||
            errorText.includes('seat is already') ||
            errorText.includes('seat already') ||
            seatError?.response?.status === 400 && (messageText.includes('seat') || errorText.includes('seat'));
          
          if (isSeatAlreadyBooked) {
            // Seat is already booked - this is a critical error
            // Note: Booking has already been created, so we need to clean it up
            // For now, throw error and let outer catch handle it
            throw new Error(`Seat ${seatNumber} is already booked by another user.Please check another Seat`);
          }
          
          // For other errors, provide detailed error message
          throw new Error(`Failed to book seat ${seatNumber}: ${errorMessage || 'Unknown error'}`);
        }
      }

      // Update payment status to ensure it matches the booking data
      console.log(`Updating payment status to: ${paymentStatus}`);
      try {
        await bookingApi.updatePaymentStatus(bookingId, paymentStatus);
        console.log('Payment status updated successfully');
      } catch (paymentError) {
        console.error('Error updating payment status:', paymentError);
        // Don't fail the entire booking if payment status update fails
        // The booking is already created successfully
      }

      console.log('All BookingSeats created successfully. Showing success message...');

      Swal.close();
      
      // Show success message
      const statusColor = paymentStatus === "Completed" ? "text-green-600" : "text-yellow-600";
      const statusText = paymentStatus === "Completed" ? "Completed" : "Pending";
      
      try {
      Swal.fire({
        icon: 'success',
        title: 'Booking Created Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>Booking Reference:</strong> BK${bookingId.toString().padStart(6, '0')}</p>
            <p class="mb-2"><strong>Payment Method:</strong> ${paymentMethod}</p>
              <p class="mb-2"><strong>Payment Status:</strong> <span class="${statusColor}">${statusText}</span></p>
            <p class="mb-2"><strong>Route:</strong> ${journeyDetails.route}</p>
              <p class="mb-2"><strong>Seats:</strong> ${selectedSeatNumbers.join(', ')}</p>
            <p class="mb-2"><strong>Total Amount:</strong> Rs ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              ${paymentStatus === "Pending" ? '<p class="mb-2 text-sm text-gray-600"><em>Payment will be collected from bus</em></p>' : ''}
          </div>
        `,
        confirmButtonColor: '#2563eb',
        confirmButtonText: 'OK'
      }).then(() => {
        // Reset form
        setSelectedSeats([]);
        setPassengerDetails({
          name: '',
          email: '',
          phone: '',
          gender: '',
          nic: ''
        });
        setPaymentMethod('Cash');
        setJourneyDetails(null);
        setSelectedScheduleId(null);
        setSeats([]);
      });
      } catch (swalError) {
        console.error('Error showing success message:', swalError);
        // Still show a simple success message even if Swal fails
        alert(`Booking created successfully! Booking ID: ${bookingId}`);
      }

    } catch (error) {
      console.error('Error creating booking:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to create booking. Please try again.',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter routes based on search term
  const filteredRoutes = routes.filter(route => {
    const fromCity = route.fromCity?.cityName || route.fromLocation || route.fromCityName || '';
    const toCity = route.toCity?.cityName || route.toLocation || route.toCityName || '';
    const searchLower = routeSearchTerm.toLowerCase();
    return fromCity.toLowerCase().includes(searchLower) || 
           toCity.toLowerCase().includes(searchLower) ||
           `${fromCity} → ${toCity}`.toLowerCase().includes(searchLower);
  });

  // Format route display
  const formatRoute = (route) => {
    const fromCity = route.fromCity?.cityName || route.fromLocation || route.fromCityName || 'N/A';
    const toCity = route.toCity?.cityName || route.toLocation || route.toCityName || 'N/A';
    return `${fromCity} → ${toCity}`;
  };

  if (!user || (!isRole(ROLES.MANAGER))) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-screen min-w-screen max-w-screen min-h-screen overflow-x-hidden bg-gray-50">
      {/* Header Section - Very Compact */}
      <section className="w-screen min-w-screen max-w-screen bg-gradient-to-r from-blue-600 to-blue-800 text-white py-2 lg:py-3">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1">
              Admin Booking
            </h1>
            <p className="text-xs sm:text-sm text-blue-100">
              Create bookings with Cash, Bank Transfer, or Collect from Bus payment
            </p>
        </div>
        </div>
      </section>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">

        {/* Route and Date Selection */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Select Route & Date</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Route Selection */}
            <div className="relative" ref={routeDropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Select Route
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search routes..."
                    value={routeSearchTerm}
                    onChange={(e) => {
                      setRouteSearchTerm(e.target.value);
                      setRouteDropdownOpen(true);
                    }}
                    onFocus={() => setRouteDropdownOpen(true)}
                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                </div>
                
                {routeDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {routesLoading ? (
                      <div className="p-4 text-center text-gray-500">Loading routes...</div>
                    ) : filteredRoutes.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No routes found</div>
                    ) : (
                      filteredRoutes.map((route) => (
                        <button
                          key={route.routeID || route.id}
                          onClick={() => {
                            setSelectedRouteId(route.routeID || route.id);
                            setRouteSearchTerm(formatRoute(route));
                            setRouteDropdownOpen(false);
                            setJourneyDetails(null);
                            setSelectedScheduleId(null);
                            setSeats([]);
                          }}
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 focus:bg-blue-50 focus:outline-none"
                        >
                          {formatRoute(route)}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Travel Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setJourneyDetails(null);
                  setSelectedScheduleId(null);
                  setSeats([]);
                }}
                min={getMinSelectableDate()}
                max={getMaxSelectableDate()}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Schedule Selection */}
          {availableSchedules.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Select Schedule
              </label>
              <select
                value={selectedScheduleId || ''}
                onChange={(e) => {
                  const scheduleId = parseInt(e.target.value);
                  setSelectedScheduleId(scheduleId);
                  const selectedSchedule = availableSchedules.find(s => (s.scheduleID || s.scheduleId) === scheduleId);
                  const route = routes.find(r => (r.routeID || r.id) == selectedRouteId);
                  if (selectedSchedule && route) {
                    processJourneyDetails(selectedSchedule, selectedSchedule.scheduleDate, route);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a schedule</option>
                {availableSchedules.map((schedule) => {
                  const time = schedule.scheduleDate?.departureTime || schedule.scheduleDate?.DepartureTime || 'N/A';
                  return (
                    <option key={schedule.scheduleID || schedule.scheduleId} value={schedule.scheduleID || schedule.scheduleId}>
                      {schedule.scheduleName || 'Standard'} - {time}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          {/* Journey Details */}
          {journeyDetails && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Route</p>
                  <p className="font-semibold">{journeyDetails.route}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Departure</p>
                  <p className="font-semibold">{journeyDetails.departure}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Arrival</p>
                  <p className="font-semibold">{journeyDetails.arrival}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Price per Seat</p>
                  <p className="font-semibold">Rs {parseFloat(journeyDetails.basePrice || 0).toLocaleString()}.00</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Seat Selection */}
        {seats.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Select Seats</h2>
            
            {/* Seat Legend */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-sm text-gray-600">Available</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-sm text-gray-600">Available Soon</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-sm text-gray-600">Gents</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-pink-500 rounded"></div>
                <span className="text-sm text-gray-600">Ladies</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-400 rounded"></div>
                <span className="text-sm text-gray-600">Not Available</span>
              </div>
            </div>

            {/* Seat Layout */}
            <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
              {(() => {
                const layoutMatch = journeyDetails.seatLayout?.match(/(\d+)[x×](\d+)/i);
                const columns = layoutMatch ? parseInt(layoutMatch[1]) : 4;
                const rows = layoutMatch ? parseInt(layoutMatch[2]) : 12;
                
                const seatsByColumn = {};
                seats.forEach(seat => {
                  if (!seatsByColumn[seat.col]) {
                    seatsByColumn[seat.col] = [];
                  }
                  seatsByColumn[seat.col].push(seat);
                });
                
                const sortedColumns = Object.keys(seatsByColumn).sort((a, b) => parseInt(a) - parseInt(b));
                const leftColumns = sortedColumns.filter(col => parseInt(col) <= Math.ceil(columns / 2));
                const rightColumns = sortedColumns.filter(col => parseInt(col) > Math.ceil(columns / 2));
                
                return (
                  <div className="flex justify-center items-start gap-4">
                    {/* Left side */}
                    <div className="flex gap-2">
                      {leftColumns.map(colNum => {
                        const columnSeats = seatsByColumn[colNum].sort((a, b) => a.row - b.row);
                        return (
                          <div key={colNum} className="flex flex-col items-center gap-2">
                            <div className="mb-2 h-6"></div>
                            {columnSeats.map((seat) => (
                              <div
                                key={seat.id}
                                className={`w-12 h-12 rounded-lg font-medium text-sm flex items-center justify-center ${getSeatClass(seat)} ${seat.isAvailable && !seat.isNotAvailable && !seat.isAvailableSoon ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                onClick={() => handleSeatClick(seat)}
                                title={seat.isAvailableSoon ? `Seat ${seat.number} - Available Soon` : `Seat ${seat.number}`}
                              >
                                {seat.number}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>

                    {/* Right side */}
                    <div className="flex gap-2">
                      {rightColumns.map(colNum => {
                        const columnSeats = seatsByColumn[colNum].sort((a, b) => a.row - b.row);
                        return (
                          <div key={colNum} className="flex flex-col items-center gap-2">
                            <div className="mb-2 h-6"></div>
                            {columnSeats.map((seat) => (
                              <div
                                key={seat.id}
                                className={`w-12 h-12 rounded-lg font-medium text-sm flex items-center justify-center ${getSeatClass(seat)} ${seat.isAvailable && !seat.isNotAvailable && !seat.isAvailableSoon ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                onClick={() => handleSeatClick(seat)}
                                title={seat.isAvailableSoon ? `Seat ${seat.number} - Available Soon` : `Seat ${seat.number}`}
                              >
                                {seat.number}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>

            {selectedSeats.length > 0 && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="font-semibold text-gray-900 mb-2">Selected Seats:</p>
                <p className="text-lg">
                  {selectedSeats.map(seatId => {
                    const seat = seats.find(s => s.id === seatId);
                    return seat ? seat.number : seatId;
                  }).join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Passenger Details & Payment Method */}
        {selectedSeats.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Passenger Details & Payment</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={passengerDetails.name}
                  onChange={(e) => setPassengerDetails({...passengerDetails, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={passengerDetails.phone}
                  onChange={(e) => setPassengerDetails({...passengerDetails, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  value={passengerDetails.gender}
                  onChange={(e) => setPassengerDetails({...passengerDetails, gender: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CreditCard className="w-4 h-4 inline mr-1" />
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="Cash">Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Collect from Bus">Collect from Bus</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  Email (Optional)
                </label>
                <input
                  type="email"
                  value={passengerDetails.email}
                  onChange={(e) => setPassengerDetails({...passengerDetails, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-1" />
                  NIC (Optional)
                </label>
                <input
                  type="text"
                  value={passengerDetails.nic}
                  onChange={(e) => setPassengerDetails({...passengerDetails, nic: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Pickup and Dropoff Locations */}
              {routeCities.length > 0 && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Pickup Location (Optional)
                    </label>
                    <select
                      value={pickupLocationId}
                      onChange={(e) => setPickupLocationId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={routeCitiesLoading}
                    >
                      <option value="">Select Pickup Location</option>
                      {routeCities.map((routeCity) => (
                        <option key={routeCity.cityID || routeCity.CityID} value={routeCity.cityID || routeCity.CityID}>
                          {routeCity.city?.cityName || routeCity.City?.CityName || routeCity.cityName || routeCity.CityName || 'Unknown'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Dropoff Location (Optional)
                    </label>
                    <select
                      value={dropoffLocationId}
                      onChange={(e) => setDropoffLocationId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={routeCitiesLoading}
                    >
                      <option value="">Select Dropoff Location</option>
                      {routeCities.map((routeCity) => (
                        <option key={routeCity.cityID || routeCity.CityID} value={routeCity.cityID || routeCity.CityID}>
                          {routeCity.city?.cityName || routeCity.City?.CityName || routeCity.cityName || routeCity.CityName || 'Unknown'}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            {/* Submit Button */}
            <div className="mt-6">
              <button
                onClick={handleBooking}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Booking...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Create Booking
                  </>
                )}
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminBooking;

